import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useToast } from '../../contexts/ToastContext';
import { formatPlanPrice } from '../../utils/planPrice';

const INTERVALS = ['monthly', 'yearly', 'triennial'];

function planToForm(plan) {
  const form = { saleLabel: plan.saleLabel || '', saleActive: !!plan.saleActive };
  INTERVALS.forEach((interval) => {
    const summary = plan[interval] || {};
    form[`${interval}Price`] = String(summary.price ?? '');
    form[`${interval}SalePrice`] = summary.salePrice != null ? String(summary.salePrice) : '';
  });
  return form;
}

function SubscriptionPlanEditor() {
  const { t } = useTranslation('subscription');
  const { plan, adminUpdatePlan, adminPayments, refreshAdminPayments } = useSubscription();
  const { addToast } = useToast();
  const [form, setForm] = useState(() => planToForm(plan));
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setForm(planToForm(plan));
  }, [plan]);

  useEffect(() => {
    refreshAdminPayments();
  }, [refreshAdminPayments]);

  const startEditing = () => {
    setError('');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setForm(planToForm(plan));
    setError('');
    setIsEditing(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    const updates = { saleLabel: form.saleLabel || null, saleActive: form.saleActive };
    INTERVALS.forEach((interval) => {
      updates[`${interval}Price`] = Number(form[`${interval}Price`]);
      updates[`${interval}SalePrice`] = form[`${interval}SalePrice`] === '' ? null : Number(form[`${interval}SalePrice`]);
    });
    const result = await adminUpdatePlan(updates);
    if (!result.success) {
      setError(result.error);
      return;
    }
    addToast(t('admin.saved'), 'success');
    setIsEditing(false);
  };

  const effectivePriceFor = (interval) => {
    const salePrice = form[`${interval}SalePrice`];
    const price = form[`${interval}Price`];
    return form.saleActive && salePrice !== '' ? Number(salePrice) : Number(price);
  };

  return (
    <div className="sheet-tables-grid">
      <div className="sheet-panel">
        <div className="sheet-panel-header">
          <span>{t('admin.panelTitle')} <span className="plan-editor-currency">{plan.currency}</span></span>
          {!isEditing && <button type="button" className="text-btn" onClick={startEditing}>{t('admin.editButton')}</button>}
        </div>
        <div className="sheet-panel-body">
          <form onSubmit={handleSubmit} className="form-card">
            <fieldset disabled={!isEditing} className="plan-editor-fieldset">
              {INTERVALS.map((interval) => (
                <div className="plan-interval-group" key={interval}>
                  <h4>{t(`interval.${interval}Label`)}</h4>
                  <div className="plan-interval-fields">
                    <label>
                      {t('admin.priceLabel')}
                      <input type="number" min="0" step="1000" value={form[`${interval}Price`]} onChange={(event) => setForm({ ...form, [`${interval}Price`]: event.target.value })} />
                    </label>
                    <label>
                      {t('admin.salePriceLabel')}
                      <input type="number" min="0" step="1000" value={form[`${interval}SalePrice`]} onChange={(event) => setForm({ ...form, [`${interval}SalePrice`]: event.target.value })} />
                    </label>
                  </div>
                  <p className="stat-caption">{t('admin.effectivePricePreview')} <strong>{formatPlanPrice(effectivePriceFor(interval), plan.currency)}</strong> {t(`interval.${interval}Period`)}</p>
                </div>
              ))}
              <label>
                {t('admin.saleLabelLabel')}
                <input value={form.saleLabel} onChange={(event) => setForm({ ...form, saleLabel: event.target.value })} placeholder={t('admin.saleLabelPlaceholder')} />
              </label>
              <label style={{ flexDirection: 'row', alignItems: 'center', gap: '0.6rem' }}>
                <input type="checkbox" checked={form.saleActive} onChange={(event) => setForm({ ...form, saleActive: event.target.checked })} style={{ width: 'auto' }} />
                {t('admin.saleToggle')}
              </label>
            </fieldset>
            <p className="stat-caption">{t('admin.grandfatherNote')}</p>
            {error && <span className="error-text">{error}</span>}
            {isEditing && (
              <div className="modal-actions">
                <button type="button" className="text-btn" onClick={cancelEditing}>{t('admin.cancelButton')}</button>
                <button type="submit" className="primary-btn standout-btn">{t('admin.saveButton')}</button>
              </div>
            )}
          </form>
        </div>
      </div>

      <div className="sheet-panel">
        <div className="sheet-panel-header"><span>{t('admin.recentPayments')}</span></div>
        <div className="sheet-panel-body">
          <div className="sheet-table-scroll">
            <table className="sheet-table">
              <thead>
                <tr>
                  <th>{t('admin.columnUser')}</th>
                  <th className="numeric">{t('admin.columnAmount')}</th>
                  <th>{t('admin.columnStatus')}</th>
                  <th>{t('admin.columnDate')}</th>
                </tr>
              </thead>
              <tbody>
                {adminPayments.length === 0 && (
                  <tr><td colSpan={4} className="goal-list-empty">{t('admin.noPayments')}</td></tr>
                )}
                {adminPayments.map((payment) => (
                  <tr key={payment.id}>
                    <td>{payment.username}</td>
                    <td className="numeric">{formatPlanPrice(payment.amount, payment.currency)}</td>
                    <td>{payment.status}</td>
                    <td>{new Date(payment.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionPlanEditor;
